import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { useGoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import { useCallback, useState } from 'react';
import { QRCode } from 'react-qrcode-logo';
import { type NavigateOptions, type To } from 'react-router';
import { useAuthContext } from 'src/auth/useAuthContext';
import Iconify from 'src/components/iconify';
import Logo from 'src/components/logo';
import { useLocales } from 'src/locales';
import { useSocketEvent, useSocketIO } from 'src/socket';
import { onLoginWithCoperaApp } from 'src/socket/listeners/login';
import { doOAuthLogin, postLogin } from 'src/utils/user-utils';
import randomUUID from 'src/utils/uuidv4';

const CustomGoogleButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: theme.palette.text.secondary,

  '&:hover': {
    backgroundColor: theme.palette.common.white,
    opacity: 0.8,
  },
}));

type Props = {
  googleGuestLogin: (user: { email: string; name: string }) => void;
};

export function JoinConferenceAuth({ googleGuestLogin }: Props) {
  const { translate } = useLocales();
  const { io } = useSocketIO();
  const { loginUser } = useAuthContext();

  const [openLoginWithCoperaApp, setOpenLoginWithCoperaApp] = useState(false);
  const [qrCodeHash, setQRCodeHash] = useState<string>('');
  const [googleError, setGoogleError] = useState<string>();

  useSocketEvent(
    onLoginWithCoperaApp(async (loginResponse) => {
      await postLogin(loginResponse, {
        navigate: (to: To | number, options?: NavigateOptions) => {},
        successCallback: async (user) => {
          await loginUser(user);
        },
      });
    }),
  );

  const onGoogleLogin = async (
    accessToken: string | undefined,
    method: 'token_info' | 'user_info',
  ) => {
    if (accessToken) {
      try {
        await doOAuthLogin({
          token: accessToken,
          method,
          signup: false,
          navigate: (to: To | number, options?: NavigateOptions) => {},
          workspaceInvite: undefined,
          successCallback: async (user) => {
            await loginUser(user);
          },
        });
      } catch (error) {
        if (error.response?.data?.user) {
          googleGuestLogin(error.response.data.user);
        }
        setGoogleError(
          error?.response?.data?.message || error?.response?.data || error,
        );
      }
    }
  };

  const onGoogleLoginError = () => {
    window.alert(translate('login.error_on_login_with_google'));
  };

  useGoogleOneTapLogin({
    onSuccess: (tokenResponse) => {
      onGoogleLogin(tokenResponse.credential, 'token_info');
    },
    onError: onGoogleLoginError,
    cancel_on_tap_outside: false,
  });

  const handleOpenLoginWithCoperaApp = () => {
    generateHashAndRegister();
    setOpenLoginWithCoperaApp(true);
  };

  const handleCloseLoginWithCoperaApp = () => {
    setOpenLoginWithCoperaApp(false);
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      onGoogleLogin(tokenResponse.access_token, 'user_info');
    },
    onError: onGoogleLoginError,
  });

  const generateHashAndRegister = useCallback(() => {
    const hash = randomUUID();
    setQRCodeHash(hash);
    io.emit('/register/device/temporary', { hash });
  }, [io]);

  return (
    <Stack gap={3}>
      <Dialog
        maxWidth="xs"
        open={openLoginWithCoperaApp}
        onClose={handleCloseLoginWithCoperaApp}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          {translate('login.login_with_copera_app')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" align="center">
            <QRCode
              value={`copera-communicate://qr-login/${qrCodeHash}`}
              logoImage="/logo/simbolo_transp_light.png"
              removeQrCodeBehindLogo
              logoPadding={2}
              fgColor="#000"
              qrStyle="dots"
            ></QRCode>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              handleCloseLoginWithCoperaApp();
            }}
          >
            {translate('shared.buttons.close')}
          </Button>
        </DialogActions>
      </Dialog>
      <CustomGoogleButton
        variant="contained"
        fullWidth
        size="large"
        startIcon={<Iconify icon="flat-color-icons:google" />}
        onClick={() => {
          setGoogleError('');
          loginWithGoogle();
        }}
      >
        {translate('communicate.meet.join_with_google')}
      </CustomGoogleButton>
      <Button
        variant="contained"
        fullWidth
        size="large"
        sx={{ backgroundColor: '#5C7DFF' }}
        startIcon={<Logo disabledLink width={20} height={20} />}
        onClick={() => {
          handleOpenLoginWithCoperaApp();
        }}
      >
        {translate('communicate.meet.join_with_copera_app')}
      </Button>

      {googleError && (
        <Typography variant="caption" color="red">
          {googleError}
        </Typography>
      )}
    </Stack>
  );
}
